/**
 * React Query hooks for ACL API
 *
 * Features:
 * - useQuery for GET operations (with caching)
 * - useMutation for POST/PUT/DELETE operations
 * - Automatic cache invalidation
 * - Loading/error state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../lib/api';
import type {
  DefaultACLSettings,
  ACLCategory,
  CreateACLCategoryRequest,
} from '../lib/api';

// ─── Query Keys ───

const ACL_KEYS = {
  all: ['acl'] as const,
  settings: () => [...ACL_KEYS.all, 'settings'] as const,
  categories: () => [...ACL_KEYS.all, 'categories'] as const,
  requests: () => [...ACL_KEYS.all, 'requests'] as const,
};

// ─── Settings Hooks ───

/**
 * Get default ACL settings
 */
export function useACLSettings() {
  return useQuery({
    queryKey: ACL_KEYS.settings(),
    queryFn: () => getApiClient().getACLSettings(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Update default ACL settings
 */
export function useUpdateACLSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: DefaultACLSettings) =>
      getApiClient().updateACLSettings(settings),
    onSuccess: (data) => {
      // Update cache with new settings
      queryClient.setQueryData(ACL_KEYS.settings(), data);
    },
  });
}

// ─── Categories Hooks ───

/**
 * Get ACL categories
 */
export function useACLCategories() {
  return useQuery({
    queryKey: ACL_KEYS.categories(),
    queryFn: () => getApiClient().getACLCategories(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create new ACL category
 */
export function useCreateACLCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: CreateACLCategoryRequest) =>
      getApiClient().createACLCategory(category),
    onSuccess: (newCategory) => {
      // Add new category to cache
      queryClient.setQueryData<ACLCategory[]>(
        ACL_KEYS.categories(),
        (old = []) => [...old, newCategory]
      );
    },
  });
}

// ─── Share Requests Hooks ───

/**
 * Get pending share requests
 */
export function useShareRequests() {
  return useQuery({
    queryKey: ACL_KEYS.requests(),
    queryFn: () => getApiClient().getShareRequests(),
    staleTime: 1000 * 30, // 30 seconds (more frequent for real-time updates)
  });
}

/**
 * Approve share request
 */
export function useApproveShareRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      getApiClient().approveShareRequest(requestId, reason),
    onSuccess: () => {
      // Invalidate pending list so approved request is removed immediately
      void queryClient.invalidateQueries({ queryKey: ACL_KEYS.requests() });
    },
  });
}

/**
 * Reject share request
 */
export function useRejectShareRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      getApiClient().rejectShareRequest(requestId, reason),
    onSuccess: () => {
      // Invalidate pending list so rejected request is removed immediately
      void queryClient.invalidateQueries({ queryKey: ACL_KEYS.requests() });
    },
  });
}

// ─── Combined Hook for Share Request Actions ───

/**
 * Combined hook for approve/reject actions
 */
export function useShareRequestActions() {
  const approveResult = useApproveShareRequest();
  const rejectResult = useRejectShareRequest();

  return {
    approve: approveResult.mutateAsync,
    reject: rejectResult.mutateAsync,
    isLoading: approveResult.isPending || rejectResult.isPending,
    /** @deprecated use approveError / rejectError for inline display */
    error: approveResult.error || rejectResult.error,
    approveError: approveResult.error,
    rejectError: rejectResult.error,
  };
}
